import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import { PageHeader, ConfirmDialog, Toast, ui } from '../components/adminUi';
import Icon from '../components/Icon';

const SEGMENTS = [
  { value: 'all', label: 'Barcha foydalanuvchilar', to: 'Barcha foydalanuvchilarga' },
  { value: 'clients', label: 'Faqat mijozlar', to: 'Faqat mijozlarga' },
  { value: 'providers', label: 'Faqat ustalar', to: 'Faqat ustalarga' },
];

export default function NotificationsPage() {
  const { auth } = useAuth();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [segment, setSegment] = useState(''); // standart tanlanmagan: admin ongli tanlaydi
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null); // { segment, count: number | null }
  const [preparing, setPreparing] = useState(false);

  // "Yuborish": avval qabul qiluvchilar sonini so'raymiz, keyin tasdiq dialogini ochamiz.
  // Endpoint xato bersa dialog sonsiz ochiladi.
  const requestSend = async () => {
    setError('');
    setResult(null);
    if (!segment) return;
    if (!title.trim() && !message.trim()) {
      setError('Sarlavha yoki matn majburiy');
      return;
    }
    setPreparing(true);
    let count = null;
    try {
      const res = await apiFetch(
        `/api/admin/notifications/recipients-count?segment=${encodeURIComponent(segment)}`,
        auth.token
      );
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        if (typeof d.count === 'number') count = d.count;
      }
    } catch {
      /* sonsiz davom etamiz */
    }
    setPreparing(false);
    setConfirm({ segment, count });
  };

  const send = async () => {
    setError('');
    setResult(null);
    setSending(true);
    try {
      const res = await apiFetch('/api/admin/notifications/push', auth.token, {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          message: message.trim(),
          segment,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const text = data.message || 'Yuborishda xatolik yuz berdi.';
        setError(text);
        setToast({ type: 'error', text });
        return;
      }
      const count = data.recipientsCount ?? data.recipients_count;
      const text = count != null
        ? `Xabar yuborildi — ${count} ta qabul qiluvchiga yetkazildi.`
        : 'Xabar muvaffaqiyatli yuborildi.';
      setResult(text);
      setToast({ type: 'success', text });
      setTitle('');
      setMessage('');
      setSegment('');
    } catch {
      const text = "Tarmoq xatosi. Qayta urinib ko'ring.";
      setError(text);
      setToast({ type: 'error', text });
    } finally {
      setSending(false);
      setConfirm(null);
    }
  };

  return (
    <AdminLayout>
      <PageHeader title="Push xabar yuborish" subtitle="Foydalanuvchilarga bildirishnoma jo'nating" />

      <div style={s.card}>
        <div style={ui.field}>
          <label style={ui.label}>Sarlavha</label>
          <input
            style={ui.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Xabar sarlavhasi"
            disabled={sending}
            maxLength={120}
          />
        </div>

        <div style={{ ...ui.field, marginBottom: 0 }}>
          <label style={ui.label}>Matn</label>
          <textarea
            style={ui.textarea}
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Xabar matni"
            disabled={sending}
          />
        </div>

        <div style={{ ...ui.field, marginTop: '24px' }}>
          <label style={ui.label}>Segment</label>
          <div style={s.segRow}>
            {SEGMENTS.map((seg) => {
              const active = segment === seg.value;
              return (
                <button
                  key={seg.value}
                  type="button"
                  onClick={() => setSegment(seg.value)}
                  style={{ ...s.segBtn, ...(active ? s.segBtnActive : {}) }}
                  disabled={sending}
                >
                  {seg.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && <div style={ui.formError}>{error}</div>}
        {result && <div style={s.success}>{result}</div>}

        <div style={{ ...ui.modalActions, justifyContent: 'flex-start', marginTop: '24px' }}>
          <button
            style={{ ...ui.saveBtn, ...s.sendBtn, ...(sending || preparing || !segment ? ui.btnDisabled : {}) }}
            onClick={requestSend}
            disabled={sending || preparing || !segment}
            title={!segment ? 'Avval segmentni tanlang' : undefined}
          >
            {sending ? 'Yuborilmoqda...' : (<><Icon name="bell" size={16} />Yuborish</>)}
          </button>
          {!segment && <span style={s.segHint}>Avval segmentni tanlang</span>}
        </div>
      </div>

      {confirm && (
        <ConfirmDialog
          title="Xabarni yuborish"
          message={`${segmentTo(confirm.segment)}${confirm.count != null ? ` (${confirm.count} ta)` : ''} yuborilsinmi?`}
          confirmLabel="Yuborish"
          busy={sending}
          onCancel={() => setConfirm(null)}
          onConfirm={send}
        />
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </AdminLayout>
  );
}

const segmentTo = (value) => (SEGMENTS.find((x) => x.value === value) || {}).to || value;

const s = {
  segHint: { fontSize: '13px', color: 'var(--color-text-muted)', marginLeft: '12px', alignSelf: 'center' },
  card: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    padding: '24px',
    maxWidth: '720px',
  },
  sendBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  segRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  segBtn: {
    height: '36px',
    padding: '0 16px',
    whiteSpace: 'nowrap',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-chip)',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  segBtnActive: {
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    borderColor: 'var(--color-primary)',
    fontWeight: '500',
  },
  success: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-success)',
    borderRadius: 'var(--radius-input)',
    padding: '12px 14px',
    color: 'var(--color-success)',
    fontSize: '14px',
    marginTop: '4px',
  },
};
